import type { RemoteObjectStore, SyncBackendKind } from "@roqeet/core";

export interface BearerObjectStoreConfig {
  accessToken: string;
  rootPath?: string;
}

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, "");
}

function joinPath(...parts: (string | undefined)[]): string {
  return parts
    .filter((part): part is string => !!part)
    .map(trimSlashes)
    .filter(Boolean)
    .join("/");
}

function encodePath(path: string): string {
  return trimSlashes(path)
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
}

function withLeadingSlash(path: string): string {
  const clean = trimSlashes(path);
  return clean ? `/${clean}` : "/";
}

async function fetchTextOrUndefined(url: string, init: RequestInit): Promise<string | undefined> {
  const res = await fetch(url, init);
  if (res.status === 404) return undefined;
  if (!res.ok) throw new Error(`${init.method ?? "GET"} ${url} failed (${res.status}): ${await res.text()}`);
  return res.text();
}

async function fetchBlobOrUndefined(url: string, init: RequestInit): Promise<Blob | undefined> {
  const res = await fetch(url, init);
  if (res.status === 404) return undefined;
  if (!res.ok) throw new Error(`${init.method ?? "GET"} ${url} failed (${res.status}): ${await res.text()}`);
  return res.blob();
}

function authHeaders(token?: string, username?: string, password?: string): HeadersInit {
  if (token) return { authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` };
  if (username !== undefined || password !== undefined) {
    return { authorization: `Basic ${btoa(`${username ?? ""}:${password ?? ""}`)}` };
  }
  return {};
}

export interface WebDavObjectStoreConfig {
  baseUrl: string;
  rootPath?: string;
  bearerToken?: string;
  username?: string;
  password?: string;
}

export class WebDavObjectStore implements RemoteObjectStore {
  readonly kind = "webdav";
  private readonly baseUrl: string;

  constructor(private readonly config: WebDavObjectStoreConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, "");
  }

  private url(path: string): string {
    return `${this.baseUrl}/${encodePath(joinPath(this.config.rootPath, path))}`;
  }

  readText(path: string): Promise<string | undefined> {
    return fetchTextOrUndefined(this.url(path), {
      method: "GET",
      headers: authHeaders(this.config.bearerToken, this.config.username, this.config.password),
    });
  }

  async writeText(path: string, text: string, contentType = "application/json"): Promise<void> {
    await this.put(path, new Blob([text], { type: contentType }));
  }

  async exists(path: string): Promise<boolean> {
    const res = await fetch(this.url(path), {
      method: "HEAD",
      headers: authHeaders(this.config.bearerToken, this.config.username, this.config.password),
    });
    if (res.status === 404) return false;
    if (!res.ok) throw new Error(`HEAD ${path} failed (${res.status}): ${await res.text()}`);
    return true;
  }

  readBlob(path: string): Promise<Blob | undefined> {
    return fetchBlobOrUndefined(this.url(path), {
      method: "GET",
      headers: authHeaders(this.config.bearerToken, this.config.username, this.config.password),
    });
  }

  writeBlob(path: string, blob: Blob, contentType = blob.type || "application/octet-stream"): Promise<void> {
    return this.put(path, new Blob([blob], { type: contentType }));
  }

  private async put(path: string, blob: Blob): Promise<void> {
    const res = await fetch(this.url(path), {
      method: "PUT",
      headers: {
        ...authHeaders(this.config.bearerToken, this.config.username, this.config.password),
        "content-type": blob.type || "application/octet-stream",
      },
      body: blob,
    });
    if (!res.ok) throw new Error(`PUT ${path} failed (${res.status}): ${await res.text()}`);
  }
}

export class DropboxObjectStore implements RemoteObjectStore {
  readonly kind = "dropbox";

  constructor(private readonly config: BearerObjectStoreConfig) {}

  private path(path: string): string {
    return withLeadingSlash(joinPath(this.config.rootPath, path));
  }

  async readText(path: string): Promise<string | undefined> {
    const blob = await this.readBlob(path);
    return blob?.text();
  }

  async writeText(path: string, text: string, contentType = "application/json"): Promise<void> {
    await this.writeBlob(path, new Blob([text], { type: contentType }));
  }

  async exists(path: string): Promise<boolean> {
    const res = await fetch("https://api.dropboxapi.com/2/files/get_metadata", {
      method: "POST",
      headers: {
        authorization: `Bearer ${this.config.accessToken}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ path: this.path(path) }),
    });
    if (res.status === 409) return false;
    if (!res.ok) throw new Error(`Dropbox metadata failed (${res.status}): ${await res.text()}`);
    return true;
  }

  async readBlob(path: string): Promise<Blob | undefined> {
    const res = await fetch("https://content.dropboxapi.com/2/files/download", {
      method: "POST",
      headers: {
        authorization: `Bearer ${this.config.accessToken}`,
        "Dropbox-API-Arg": JSON.stringify({ path: this.path(path) }),
      },
    });
    if (res.status === 409) return undefined;
    if (!res.ok) throw new Error(`Dropbox download failed (${res.status}): ${await res.text()}`);
    return res.blob();
  }

  async writeBlob(path: string, blob: Blob): Promise<void> {
    const res = await fetch("https://content.dropboxapi.com/2/files/upload", {
      method: "POST",
      headers: {
        authorization: `Bearer ${this.config.accessToken}`,
        "content-type": "application/octet-stream",
        "Dropbox-API-Arg": JSON.stringify({
          path: this.path(path),
          mode: "overwrite",
          autorename: false,
          mute: true,
        }),
      },
      body: blob,
    });
    if (!res.ok) throw new Error(`Dropbox upload failed (${res.status}): ${await res.text()}`);
  }
}

export interface OneDriveObjectStoreConfig extends BearerObjectStoreConfig {
  driveId?: string;
}

export class OneDriveObjectStore implements RemoteObjectStore {
  readonly kind = "onedrive";

  constructor(private readonly config: OneDriveObjectStoreConfig) {}

  private base(path: string): string {
    const fullPath = encodePath(joinPath(this.config.rootPath, path));
    const drive = this.config.driveId ? `/drives/${encodeURIComponent(this.config.driveId)}` : "/me/drive";
    return `https://graph.microsoft.com/v1.0${drive}/root:/${fullPath}`;
  }

  readText(path: string): Promise<string | undefined> {
    return fetchTextOrUndefined(`${this.base(path)}:/content`, {
      method: "GET",
      headers: { authorization: `Bearer ${this.config.accessToken}` },
    });
  }

  async writeText(path: string, text: string, contentType = "application/json"): Promise<void> {
    await this.writeBlob(path, new Blob([text], { type: contentType }));
  }

  async exists(path: string): Promise<boolean> {
    const res = await fetch(this.base(path), {
      method: "GET",
      headers: { authorization: `Bearer ${this.config.accessToken}` },
    });
    if (res.status === 404) return false;
    if (!res.ok) throw new Error(`OneDrive metadata failed (${res.status}): ${await res.text()}`);
    return true;
  }

  readBlob(path: string): Promise<Blob | undefined> {
    return fetchBlobOrUndefined(`${this.base(path)}:/content`, {
      method: "GET",
      headers: { authorization: `Bearer ${this.config.accessToken}` },
    });
  }

  async writeBlob(path: string, blob: Blob): Promise<void> {
    const res = await fetch(`${this.base(path)}:/content`, {
      method: "PUT",
      headers: {
        authorization: `Bearer ${this.config.accessToken}`,
        "content-type": blob.type || "application/octet-stream",
      },
      body: blob,
    });
    if (!res.ok) throw new Error(`OneDrive upload failed (${res.status}): ${await res.text()}`);
  }
}

export interface GitHubObjectStoreConfig {
  token: string;
  owner: string;
  repo: string;
  branch?: string;
  rootPath?: string;
  committer?: { name: string; email: string };
}

export class GitHubObjectStore implements RemoteObjectStore {
  readonly kind = "github";
  private readonly apiBase: string;

  constructor(private readonly config: GitHubObjectStoreConfig) {
    this.apiBase = `https://api.github.com/repos/${encodeURIComponent(config.owner)}/${encodeURIComponent(config.repo)}/contents`;
  }

  private path(path: string): string {
    return encodePath(joinPath(this.config.rootPath, path));
  }

  private async getContent(path: string): Promise<{ content: string; sha: string; encoding: string } | undefined> {
    const params = this.config.branch ? `?ref=${encodeURIComponent(this.config.branch)}` : "";
    const res = await fetch(`${this.apiBase}/${this.path(path)}${params}`, {
      headers: {
        authorization: `Bearer ${this.config.token}`,
        accept: "application/vnd.github+json",
      },
    });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(`GitHub content read failed (${res.status}): ${await res.text()}`);
    return (await res.json()) as { content: string; sha: string; encoding: string };
  }

  async readText(path: string): Promise<string | undefined> {
    const item = await this.getContent(path);
    if (!item) return undefined;
    return atob(item.content.replace(/\s/g, ""));
  }

  async writeText(path: string, text: string): Promise<void> {
    await this.writeBase64(path, btoa(text));
  }

  async exists(path: string): Promise<boolean> {
    return (await this.getContent(path)) !== undefined;
  }

  async readBlob(path: string): Promise<Blob | undefined> {
    const item = await this.getContent(path);
    if (!item) return undefined;
    const binary = atob(item.content.replace(/\s/g, ""));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes]);
  }

  async writeBlob(path: string, blob: Blob): Promise<void> {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    await this.writeBase64(path, btoa(binary));
  }

  private async writeBase64(path: string, content: string): Promise<void> {
    const existing = await this.getContent(path);
    const res = await fetch(`${this.apiBase}/${this.path(path)}`, {
      method: "PUT",
      headers: {
        authorization: `Bearer ${this.config.token}`,
        accept: "application/vnd.github+json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        message: `Update ${path}`,
        content,
        ...(this.config.branch ? { branch: this.config.branch } : {}),
        ...(existing?.sha ? { sha: existing.sha } : {}),
        ...(this.config.committer ? { committer: this.config.committer } : {}),
      }),
    });
    if (!res.ok) throw new Error(`GitHub content write failed (${res.status}): ${await res.text()}`);
  }
}

export interface PCloudObjectStoreConfig extends BearerObjectStoreConfig {
  apiBaseUrl?: string;
}

export class PCloudObjectStore implements RemoteObjectStore {
  readonly kind = "pcloud";
  private readonly apiBase: string;

  constructor(private readonly config: PCloudObjectStoreConfig) {
    this.apiBase = (config.apiBaseUrl ?? "https://api.pcloud.com").replace(/\/+$/, "");
  }

  private path(path: string): string {
    return withLeadingSlash(joinPath(this.config.rootPath, path));
  }

  private authParams(path: string): URLSearchParams {
    return new URLSearchParams({ access_token: this.config.accessToken, path: this.path(path) });
  }

  async readText(path: string): Promise<string | undefined> {
    const blob = await this.readBlob(path);
    return blob?.text();
  }

  async writeText(path: string, text: string, contentType = "application/json"): Promise<void> {
    await this.writeBlob(path, new Blob([text], { type: contentType }));
  }

  async exists(path: string): Promise<boolean> {
    const res = await fetch(`${this.apiBase}/stat?${this.authParams(path)}`);
    if (res.status === 404) return false;
    const json = await res.json() as { result?: number };
    return json.result === 0;
  }

  async readBlob(path: string): Promise<Blob | undefined> {
    const params = this.authParams(path);
    const linkRes = await fetch(`${this.apiBase}/getfilelink?${params}`);
    if (linkRes.status === 404) return undefined;
    if (!linkRes.ok) throw new Error(`pCloud link failed (${linkRes.status}): ${await linkRes.text()}`);
    const link = await linkRes.json() as { result?: number; hosts?: string[]; path?: string };
    if (link.result !== 0 || !link.hosts?.[0] || !link.path) return undefined;
    return fetchBlobOrUndefined(`https://${link.hosts[0]}${link.path}`, { method: "GET" });
  }

  async writeBlob(path: string, blob: Blob): Promise<void> {
    const clean = this.path(path);
    const slash = clean.lastIndexOf("/");
    const dir = clean.slice(0, slash) || "/";
    const filename = clean.slice(slash + 1);
    const form = new FormData();
    form.append("file", blob, filename);
    const params = new URLSearchParams({
      access_token: this.config.accessToken,
      path: dir,
      filename,
      nopartial: "1",
    });
    const res = await fetch(`${this.apiBase}/uploadfile?${params}`, { method: "POST", body: form });
    if (!res.ok) throw new Error(`pCloud upload failed (${res.status}): ${await res.text()}`);
    const json = await res.json() as { result?: number; error?: string };
    if (json.result !== 0) throw new Error(`pCloud upload failed: ${json.error ?? json.result}`);
  }
}

export interface S3CompatibleObjectStoreConfig {
  endpoint: string;
  bucket: string;
  region: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  rootPath?: string;
  forcePathStyle?: boolean;
}

export class S3CompatibleObjectStore implements RemoteObjectStore {
  readonly kind = "s3-compatible";
  private readonly endpoint: URL;

  constructor(private readonly config: S3CompatibleObjectStoreConfig) {
    this.endpoint = new URL(config.endpoint);
  }

  private objectKey(path: string): string {
    return joinPath(this.config.rootPath, path);
  }

  private url(path: string): URL {
    const key = encodePath(this.objectKey(path));
    if (this.config.forcePathStyle) {
      return new URL(`/${encodeURIComponent(this.config.bucket)}/${key}`, this.endpoint);
    }
    const url = new URL(`/${key}`, this.endpoint);
    url.hostname = `${this.config.bucket}.${url.hostname}`;
    return url;
  }

  readText(path: string): Promise<string | undefined> {
    return this.signedFetch(path, "GET").then(async (res) => {
      if (res.status === 404) return undefined;
      if (!res.ok) throw new Error(`S3 GET failed (${res.status}): ${await res.text()}`);
      return res.text();
    });
  }

  async writeText(path: string, text: string, contentType = "application/json"): Promise<void> {
    await this.writeBlob(path, new Blob([text], { type: contentType }));
  }

  async exists(path: string): Promise<boolean> {
    const res = await this.signedFetch(path, "HEAD");
    if (res.status === 404) return false;
    if (!res.ok) throw new Error(`S3 HEAD failed (${res.status}): ${await res.text()}`);
    return true;
  }

  async readBlob(path: string): Promise<Blob | undefined> {
    const res = await this.signedFetch(path, "GET");
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(`S3 GET failed (${res.status}): ${await res.text()}`);
    return res.blob();
  }

  async writeBlob(path: string, blob: Blob): Promise<void> {
    const res = await this.signedFetch(path, "PUT", blob);
    if (!res.ok) throw new Error(`S3 PUT failed (${res.status}): ${await res.text()}`);
  }

  private async signedFetch(path: string, method: "GET" | "HEAD" | "PUT", body?: Blob): Promise<Response> {
    const url = this.url(path);
    const payload = body ? new Uint8Array(await body.arrayBuffer()) : new Uint8Array();
    const payloadHash = await sha256Hex(payload);
    const now = new Date();
    const amzDate = toAmzDate(now);
    const date = amzDate.slice(0, 8);
    const headers: Record<string, string> = {
      host: url.host,
      "x-amz-content-sha256": payloadHash,
      "x-amz-date": amzDate,
    };
    if (this.config.sessionToken) headers["x-amz-security-token"] = this.config.sessionToken;
    if (body?.type) headers["content-type"] = body.type;

    const signedHeaderNames = Object.keys(headers).sort();
    const canonicalHeaders = signedHeaderNames.map((name) => `${name}:${headers[name]}\n`).join("");
    const canonicalRequest = [
      method,
      url.pathname,
      url.searchParams.toString(),
      canonicalHeaders,
      signedHeaderNames.join(";"),
      payloadHash,
    ].join("\n");
    const credentialScope = `${date}/${this.config.region}/s3/aws4_request`;
    const stringToSign = [
      "AWS4-HMAC-SHA256",
      amzDate,
      credentialScope,
      await sha256Hex(canonicalRequest),
    ].join("\n");
    const signingKey = await s3SigningKey(this.config.secretAccessKey, date, this.config.region);
    const signature = await hmacHex(signingKey, stringToSign);
    headers.authorization =
      `AWS4-HMAC-SHA256 Credential=${this.config.accessKeyId}/${credentialScope}, ` +
      `SignedHeaders=${signedHeaderNames.join(";")}, Signature=${signature}`;

    return fetch(url, {
      method,
      headers,
      ...(body ? { body } : {}),
    });
  }
}

export class UnsupportedDirectObjectStore implements RemoteObjectStore {
  readonly kind: SyncBackendKind;

  constructor(kind: "mega" | "proton-drive") {
    this.kind = kind;
  }

  private fail<T>(): Promise<T> {
    return Promise.reject(new Error(`${this.kind} direct sync requires a browser-capable provider SDK. RoQeet must not proxy user files through its servers.`));
  }

  readText(_path: string): Promise<string | undefined> {
    return this.fail();
  }
  writeText(_path: string, _text: string): Promise<void> {
    return this.fail();
  }
  exists(_path: string): Promise<boolean> {
    return this.fail();
  }
}

async function sha256Hex(input: Uint8Array | string): Promise<string> {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input;
  const digest = await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes));
  return hex(new Uint8Array(digest));
}

async function hmacBytes(key: ArrayBuffer | Uint8Array, data: string): Promise<Uint8Array> {
  const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(keyBytes),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return new Uint8Array(await crypto.subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(data)));
}

async function hmacHex(key: Uint8Array, data: string): Promise<string> {
  return hex(await hmacBytes(key, data));
}

async function s3SigningKey(secret: string, date: string, region: string): Promise<Uint8Array> {
  const kDate = await hmacBytes(new TextEncoder().encode(`AWS4${secret}`), date);
  const kRegion = await hmacBytes(kDate, region);
  const kService = await hmacBytes(kRegion, "s3");
  return hmacBytes(kService, "aws4_request");
}

function toAmzDate(date: Date): string {
  return date.toISOString().replace(/[:-]|\.\d{3}/g, "");
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(out).set(bytes);
  return out;
}
